import React, { useEffect, useState } from "react";
import { Play, Loader2, CheckCircle2, XCircle, ArrowDown } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { PageHeader, Card, Loading, SandboxTag } from "../components/common";

export default function Simulator() {
  const { t } = useI18n();
  const [ucs, setUcs] = useState(null);
  const [selected, setSelected] = useState("");
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/usecases", { params: { limit: 1000 } }).then((r) => { setUcs(r.data); setSelected(r.data[0]?.id); }); }, []);
  if (!ucs) return <Loading />;

  const run = async () => {
    if (!selected) return;
    setBusy(true); setSim(null);
    try { const { data } = await api.post(`/usecases/${selected}/simulate`); setSim(data); } finally { setBusy(false); }
  };

  const GATEWAY = ["USER", "PROMPT GUARDIAN", "DLP / PRIVACY", "POLICY CHECK", "MODEL GUARDIAN", "AI MODEL", "RESPONSE INSPECTION", "AGENT GUARDIAN", "AI GATEWAY", "APPLICATION"];

  return (
    <div>
      <PageHeader title={t("nav.simulator")} subtitle="AI Gateway simulator — Prompt / Model / Agent Guardians" summary="Sandbox simulator. Run a governed AI request through the simulated gateway pipeline. All results are synthetic.">
        <SandboxTag />
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Run Simulation" className="lg:col-span-1">
          <label className="mb-1 block text-xs font-semibold text-slate-600">Select Use Case</label>
          <select data-testid="sim-select" value={selected} onChange={(e) => setSelected(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[var(--qatar)]">
            {ucs.map((u) => <option key={u.id} value={u.id}>{u.id} — {u.name_en}</option>)}
          </select>
          <button data-testid="sim-run" onClick={run} disabled={busy} className="qatar-gradient mt-3 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {t("run_demo")}
          </button>

          <div className="mt-5">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">AI Gateway Flow</p>
            <div className="space-y-1">
              {GATEWAY.map((g, i) => (
                <React.Fragment key={g}>
                  <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-1.5 text-center text-xs font-medium text-slate-600">{g}</div>
                  {i < GATEWAY.length - 1 && <div className="flex justify-center"><ArrowDown className="h-3 w-3 text-slate-300" /></div>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Card>

        <div className="lg:col-span-2">
          {sim ? (
            <Card title="Simulation Trace" right={<SandboxTag />}>
              <div className="mb-4"><span className={`rounded-lg px-4 py-1.5 text-sm font-bold ${sim.result === "APPROVED" ? "bg-emerald-100 text-emerald-700" : sim.result === "FAILED SECURITY" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid="sim-result">{sim.result}</span></div>
              <ol className="space-y-2">
                {sim.steps.map((st, i) => (
                  <li key={i} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2.5 text-sm">
                    {st.status === "BLOCK" ? <XCircle className="h-5 w-5 shrink-0 text-red-500" /> : <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" />}
                    <span className="w-44 shrink-0 font-semibold text-slate-800">{st.step}</span>
                    <span className="text-xs text-slate-500">{st.detail}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-3 text-xs text-slate-400">✓ Audit event & monitoring record created for this simulation.</p>
            </Card>
          ) : (
            <Card><div className="grid place-items-center py-24 text-slate-400"><Play className="mb-3 h-10 w-10" /><p className="text-sm">Select a use case and run the sandbox simulation.</p></div></Card>
          )}
        </div>
      </div>
    </div>
  );
}
